import { useLayoutEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import "./App.css";

import Header from "./pages/Header";
import Footer from "./pages/Footer";

import HomePage from "./pages/Home";
import Resources from "./pages/Resources";
import Pricing from "./pages/Pricing";
import Contactus from "./pages/Contactus";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AboutUs from "./pages/AboutUs";
import Contact from "./pages/Contact";

import BlogIndex from "./blog/BlogIndex"; // ✅ NEW (real /blog page)
import BlogPost from "./blog/BlogPost";

import CustomerGallery from "./pages/CustomerGallery";
import CustomerCaseStudy from "./pages/CustomerCaseStudy";

import JobFestPage from "./pages/JobFestPage";

import JobFestOnePager from "./pages/JobFestOnePager";
import MTW from "./pages/MTW";
import BookDemo from "./pages/BookDemo";
import SolutionPage from "./pages/SolutionPage";
import UseCasePage from "./pages/UseCasePage";

/* ✅ centralized SEO */
import Seo from "./seo/Seo";
import { ROUTE_SEO } from "./seo/routes";
import ProductPage from "./pages/ProductPage";
import ApiPage from "./pages/ApiPage";
import HiringCircuit from "./pages/HiringCircuit";
import AiTalentDriveCompanies from "./pages/AiTalentDriveCompanies";
import DriveTeamJoin from "./pages/DriveTeamJoin";
import AiTalentDriveBanner from "./components/AiTalentDriveBanner";

function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);
  useLayoutEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;

    // Skip scroll-to-top for pricing tab switches
    if (prev.startsWith("/pricing/") && pathname.startsWith("/pricing/")) {
      return;
    }

    // Disable browser's automatic scroll restoration
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

/* The AI Talent Drive page launched at /hiring-circuit. Links already shared,
   search results and the payment gateway's return URL (see the backend's
   hiring_circuit_views._page_url) still point there, so keep it working —
   the #register?order=... fragment must survive the redirect. */
function HiringCircuitRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/ai-talent-drive", search, hash }} replace />;
}

function App() {
  const location = useLocation();

  /* normalize path */
  const path = location.pathname.replace(/\/+$/, "") || "/";

  /* The site header persists on every page except the two AI Talent Drive
     pages (see hideHeader). Those two (the candidate page and the company
     sign-up) also skip the promo banner, since each renders the same graphic
     as its own hero, and swap the site footer for their own. */
  const isDrivePage =
    path === "/ai-talent-drive" ||
    path === "/ai-talent-drive/hiring-companies" ||
    path.startsWith("/ai-talent-drive/join/");

  /* The drive's own pages stand alone: no site header, no spacer. The team
     join link (/ai-talent-drive/join/:token) keeps it. */
  const hideHeader = path === "/ai-talent-drive" || path === "/ai-talent-drive/hiring-companies";

  /* pages that manage their own SEO */
  const isBlogPost = /^\/blog\/[^/]+$/.test(path);
  const isCustomerCaseStudy = /^\/customers\/[^/]+$/.test(path);

  const pageSeo =
    !isBlogPost && !isCustomerCaseStudy ? ROUTE_SEO[path] : undefined;

  return (
    <div className="relative min-h-screen w-full bg-black text-base text-white">
      <ScrollToTop />
      {!hideHeader && <Header />}
      {!hideHeader && <div className="h-16 lg:h-[72px]" />} {/* spacer for the fixed header */}
      {/* Event banner under the header; the drive page shows it as its hero */}
      {!isDrivePage && <AiTalentDriveBanner />}

      {/* global SEO only for static routes */}
      {pageSeo && <Seo {...pageSeo} url={path} />}

      <Routes>
        {/* home */}
        <Route path="/" element={<HomePage />} />
        {/* static pages */}
        <Route path="/resources" element={<Resources />} />
        <Route path="/resources/aboutus" element={<AboutUs />} />
        <Route path="/resources/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pricing/:tab" element={<Pricing />} />
        <Route path="/contactus" element={<Contactus />} />
        <Route path="/termsandconditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        {/* blog */}
        <Route path="/blog" element={<BlogIndex />} /> {/* ✅ REQUIRED */}
        <Route path="/blog/:slug" element={<BlogPost />} />
        {/* customers */}
        <Route path="/customers" element={<CustomerGallery />} />
        <Route path="/customers/:slug" element={<CustomerCaseStudy />} />
        {/* job fest */}
        <Route path="/ai-talent-fest" element={<JobFestPage />} />
        <Route path="/aitalentfest" element={<JobFestOnePager />} />
        {/* MTW standalone */}
        <Route path="/mtw" element={<MTW />} />
        {/* demo */}
        <Route path="/book-demo" element={<BookDemo />} />
        {/* API */}
        <Route path="/api" element={<ApiPage />} />
        {/* AI Talent Drive event page (formerly /hiring-circuit) */}
        <Route path="/ai-talent-drive" element={<HiringCircuit />} />
        <Route path="/ai-talent-drive/hiring-companies" element={<AiTalentDriveCompanies />} />
        {/* Team sign-up link handed to hiring companies (token in the path) */}
        <Route path="/ai-talent-drive/join/:token" element={<DriveTeamJoin />} />
        <Route path="/hiring-circuit" element={<HiringCircuitRedirect />} />
        {/* Product Pages */}
        <Route path="/product/:slug" element={<ProductPage />} />
        {/* Solutions & Use Cases Pages */}
        <Route path="/solutions/:slug" element={<SolutionPage />} />
        <Route path="/use-cases/:slug" element={<UseCasePage />} />
        {/* fallback */}
        <Route path="*" element={<HomePage />} />
      </Routes>

      {!isDrivePage && <Footer />}
    </div>
  );
}

export default App;
